import { Input } from "antd";
import { ChatContext } from "context/ChatContext";
import { useCallback, useContext, useEffect, useState } from "react";
import { BsArchive, BsChat, BsChatQuote, BsSearch } from "react-icons/bs";
import { useSelector } from "react-redux";
import AllChatSelectorIcon from "./AllChatSelectorIcon";
import ChatList from "./ChatList";
import { store } from "redux/store";
import { useAppSelector } from "app/hooks";

function AllChats({ setChatIsOpen, setSelectedChat, sidePanel = false }: any) {
  const { allChats } = useAppSelector((state: any) => state?.userChat);
  const { isLoading, unreadChats } = useContext(ChatContext);
  const loggedInUser = useSelector((state: any) => state?.user?.currentUser);

  const [selectedChatList, setSelectedChatList] = useState<String>("active");
  const [approvedChats, setApprovedChats] = useState<Array<any>>([]);
  const [pendingChats, setPendingChats] = useState<Array<any>>([]);
  const [archivedChats, setArchivedChats] = useState<Array<any>>([]);
  const [searchText, setSeachText] = useState<string>("");

  const extractApprovedChats = () => {
    const approved = allChats?.filter((chat: any) => {
      return (
        (chat?.status === "approved" && chat.block_status === "active") ||
        (["rejected", "pending"].includes(chat?.status) &&
          chat?.chat_name.split("-")[0] == loggedInUser?.id &&
          chat?.block_status !== "blocked") ||
        (chat.block_status === "blocked" &&
          !chat.block_by.includes(loggedInUser?.id))
      );
    });
    setApprovedChats(approved);
  };

  const extractPendingChats = () => {
    const pendingChatsChats = allChats?.filter((chat: any) => {
      return (
        chat.status === "pending" &&
        chat?.chat_name.split("-")[0] != loggedInUser?.id
      );
    });
    setPendingChats(pendingChatsChats);
  };

  // TODO: get blocked chats
  const extractBlockedChat = () => {
    const blockedChats = allChats?.filter((chat: any) => {
      return (
        (chat?.status === "rejected" &&
          chat?.chat_name.split("-")[1] == loggedInUser?.id) ||
        (chat.block_status === "blocked" &&
          chat.block_by.includes(loggedInUser?.id))
      );
    });
    setArchivedChats(blockedChats);
  };

  const calculateTotalUnreadChatMsgCount = useCallback(() => {
    // Get common objects by id
    const commonObjects = unreadChats.filter((obj1: any) =>
      approvedChats.some((obj2: any) => obj1?.chatId == obj2.id)
    );
    let total = 0;
    commonObjects?.map((chat: any) => {
      total += chat?.count;
    });
    return total;
  }, [unreadChats]);

  useEffect(() => {
    extractApprovedChats();
    extractPendingChats();
    extractBlockedChat();
  }, [JSON.stringify(allChats)]);

  useEffect(() => {
    if (!searchText) {
      if (
        (selectedChatList === "request" && pendingChats?.length == 0) ||
        (selectedChatList === "archive" && archivedChats?.length == 0)
      ) {
        setSelectedChatList("active");
      }
    }
  }, [pendingChats, archivedChats]);

  useEffect(() => {
    if (searchText) {
      if (selectedChatList === "active") {
        filterApprovedChats();
      } else if (selectedChatList === "request") {
        filterPendingChats();
      } else {
        filterArchivedChats();
      }
    } else {
      extractApprovedChats();
      extractBlockedChat();
      extractPendingChats();
    }
  }, [searchText]);

  useEffect(() => {
    extractApprovedChats();
    extractBlockedChat();
    extractPendingChats();
  }, [selectedChatList]);

  const filterApprovedChats = () => {
    const filteredChats = allChats?.filter((chat: any) => {
      return (
        chat?.receiver?.name
          ?.toLowerCase()
          .includes(searchText.toLowerCase()) &&
        (chat?.status === "approved" ||
          (chat.status === "pending" &&
            chat?.chat_name.split("-")[0] == loggedInUser?.id))
      );
    });
    setApprovedChats(filteredChats);
  };

  const filterPendingChats = () => {
    const filteredChats = allChats?.filter((chat: any) => {
      return (
        chat?.receiver?.name
          ?.toLowerCase()
          .includes(searchText.toLowerCase()) &&
        chat.status === "pending" &&
        chat?.chat_name.split("-")[0] != loggedInUser?.id
      );
    });
    setPendingChats(filteredChats);
  };

  const filterArchivedChats = () => {
    const filteredChats = allChats?.filter((chat: any) => {
      return (
        chat?.receiver?.name
          ?.toLowerCase()
          .includes(searchText.toLowerCase()) && chat.block_status === "blocked"
      );
    });
    setArchivedChats(filteredChats);
  };

  return (
    <>
      <div
        className={`w-full ${
          sidePanel ? "h-fit p-[15px]" : "h-[85vh] p-0"
        } relative bg-[var(--Color1E1E1E)] sm:rounded-2xl`}
      >
        {/* Search */}
        <div className="h-[8%]">
          <Input
            size="middle"
            placeholder="Search chats"
            prefix={<BsSearch className="mx-1" />}
            onChange={(e) => setSeachText(e.target.value)}
            className="chat-search-input"
            allowClear
          />
        </div>

        {/* chat list select icons (segmented control) */}
        <div
          className={`flex h-[10%] items-center justify-center ${
            sidePanel ? `pb-2 pt-4` : `pb-2 pt-3`
          }`}
        >
          <div className="inline-flex items-center gap-1 rounded-full border border-white/10 bg-white/5 p-1">
            <AllChatSelectorIcon
              activated={approvedChats?.length > 0 ? true : false}
              Icon={<BsChat />}
              tooltipTitle="Active Chats"
              selected={selectedChatList === "active"}
              handleClick={() => {
                setSelectedChatList("active");
              }}
              chatCount={calculateTotalUnreadChatMsgCount()}
            />

            <AllChatSelectorIcon
              activated={pendingChats?.length > 0 ? true : false}
              Icon={<BsChatQuote />}
              tooltipTitle="Chat Requests"
              selected={selectedChatList === "request"}
              handleClick={() => {
                setSelectedChatList("request");
              }}
              chatCount={pendingChats?.length}
            />

            <AllChatSelectorIcon
              activated={archivedChats?.length > 0 ? true : false}
              Icon={<BsArchive />}
              tooltipTitle="Archive Chats"
              selected={selectedChatList === "archive"}
              handleClick={() => {
                setSelectedChatList("archive");
              }}
              chatCount={archivedChats?.length}
            />
          </div>
        </div>

        {/* chat list */}
        <div className="h-[82%] overflow-y-scroll scrollbar-hide">
          {selectedChatList === "active" ? (
            isLoading ? (
              <div className="flex h-full items-center justify-center">
                <div className="h-7 w-7 animate-spin rounded-full border-2 border-white/15 border-t-[var(--red,#F50537)]" />
              </div>
            ) : (
              <ChatList
                chatList={approvedChats}
                setSelectedChat={setSelectedChat}
                setChatIsOpen={setChatIsOpen}
              />
            )
          ) : selectedChatList === "request" ? (
            <ChatList
              chatList={pendingChats}
              setSelectedChat={setSelectedChat}
              setChatIsOpen={setChatIsOpen}
            />
          ) : (
            <ChatList
              chatList={archivedChats}
              setSelectedChat={setSelectedChat}
              setChatIsOpen={setChatIsOpen}
            />
          )}
        </div>
      </div>

      {/* themed search input (dark, pill) */}
      <style>{`
        .chat-search-input.ant-input-affix-wrapper {
          background: rgba(255, 255, 255, 0.06);
          border: 1px solid rgba(255, 255, 255, 0.12);
          border-radius: 9999px;
          padding-top: 6px;
          padding-bottom: 6px;
        }
        .chat-search-input.ant-input-affix-wrapper:hover,
        .chat-search-input.ant-input-affix-wrapper-focused {
          border-color: rgba(255, 255, 255, 0.3);
          box-shadow: none;
        }
        .chat-search-input .ant-input {
          background: transparent;
          color: #fff;
        }
        .chat-search-input .ant-input::placeholder {
          color: rgba(255, 255, 255, 0.4);
        }
        .chat-search-input .ant-input-prefix,
        .chat-search-input .anticon {
          color: rgba(255, 255, 255, 0.5);
        }
      `}</style>
    </>
  );
}

export default AllChats;